import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { Link } from "react-router-dom";

import LoadingSpinner from '../partials/LoadingSpinner';

import { orderShipmentsIndex, orderShipmentPrepare, orderShipmentsPrepareAll, orderShipmentsPrepareReset, orderShipmentsConvertToTotes } from '../../redux/actions/orderShipments';
import { totesIndex, assignTotesToBatches, totesUndo, totesDelete } from '../../redux/actions/totes';
import { cartLayoutsIndex } from '../../redux/actions/cartLayouts';
import { locationsIndex } from '../../redux/actions/locations';
import { getSettings } from '../../redux/actions/settings';
import { isDefaultCartLayout, UI_COLORS } from '../../constants';

import {
  Page, Layout, Card, BlockStack, InlineStack, Text, Button, Select,
  IndexTable, useIndexResourceState, Modal, Banner, Tooltip, Icon, Box,
} from '@shopify/polaris';
import { RefreshIcon, UploadIcon, DeleteIcon, UndoIcon, TransferInternalIcon, AlertTriangleIcon } from '@shopify/polaris-icons';

const OrderShipments = ({ orderShipments, totes, cartLayouts, locations, settings, loadOrderShipments, loadTotes, loadCartLayouts, loadLocations, loadSettings, prepareOrderShipmentPage, prepareAllOrderShipments, resetPrepare, convertOrderShipmentsToTotes, assignTotes, undoTotes, deleteTotes }) => {
  const [pendingToteAction, setPendingToteAction] = useState(null);
  const [selectedLayout, setSelectedLayout] = useState(null);
  const [assignedToBatches, setAssignedToBatches] = useState(false);
  const [selectedDetail, setSelectedDetail] = useState(null);

  useEffect(() => {
    loadOrderShipments();
    loadTotes();
    loadCartLayouts();
    loadLocations();
    loadSettings();
  }, [loadOrderShipments, loadTotes, loadCartLayouts, loadLocations, loadSettings]);

  useEffect(() => {
    if (cartLayouts.cartLayouts.length > 0 && !selectedLayout) {
      const defaultLayout = cartLayouts.cartLayouts.find(layout => layout.id === settings.settings?.default_cart_layout);
      const fallbackLayout = cartLayouts.cartLayouts.find(isDefaultCartLayout);
      setSelectedLayout(defaultLayout || fallbackLayout || cartLayouts.cartLayouts[0]);
    }
  }, [cartLayouts, settings, selectedLayout]);

  const isBusy = orderShipments.isFetching || orderShipments.isFetchingMore || orderShipments.isPreparing || orderShipments.isPreparingAll || orderShipments.isConverting;
  const totesBusy = totes.isFetching || totes.isFetchingMore || totes.isUndoing || totes.isDeleting || totes.isAssigning;

  const locationLabel = (locationId) => {
    const match = (locations.locations || []).find((location) => String(location.id) === String(locationId));
    return match?.label || (locationId ? `#${locationId}` : '-');
  }

  const destination = (shipment) => {
    const address = shipment?.shipping_address || {};
    const name = [address.first_name, address.last_name].filter(Boolean).join(' ');
    const cityState = [address.city, address.state].filter(Boolean).join(', ');

    return [address.company, name, cityState].filter(Boolean).join(' • ') || 'No shipping address';
  }

  const formatAddress = (address) => {
    if (!address) return ['No shipping address recorded.'];

    const name = [address.first_name, address.last_name].filter(Boolean).join(' ');
    const lines = [
      name,
      address.company,
      address.street_1,
      address.street_2,
      [address.city, address.state, address.zip].filter(Boolean).join(', '),
      address.country
    ].filter((line) => line && line !== '');

    return lines.length > 0 ? lines : ['No shipping address recorded.'];
  }

  const totalQuantity = (items) => {
    return items.reduce((total, item) => total + (Number(item.quantity) || 0), 0);
  }

  // Reasons a shipment can't be converted under the given cart layout.
  // Each blocker is a { code, message } - add new checks here as other
  // conversion constraints come up.
  const shipmentBlockers = (items, layout) => {
    const blockers = [];
    if (!layout) return blockers;

    const units = totalQuantity(items);
    const positions = (Number(layout.rows) || 0) * (Number(layout.columns) || 0);
    const perTote = Number(layout.max_items_per_tote) || 0;
    const perBatch = Number(layout.max_items_per_batch) || 0;

    // A shipment is partitioned across ceil(units / perTote) totes and a
    // cart only has rows * columns positions - nil perTote means unlimited
    if (perTote > 0) {
      const totesNeeded = Math.ceil(units / perTote);
      if (totesNeeded > positions) {
        blockers.push({
          code: 'exceeds_cart_capacity',
          message: `Needs ${totesNeeded} totes for ${units} items, but this cart layout only has ${positions} tote${positions === 1 ? '' : 's'}.`
        });
      }
    }

    // Batches never split a tote and shipments can't span batches, so the
    // shipment's units must fit under the layout's per-batch item limit
    if (perBatch > 0 && units > perBatch) {
      blockers.push({
        code: 'exceeds_batch_limit',
        message: `Has ${units} items, over this cart layout's ${perBatch}-item batch limit.`
      });
    }

    return blockers;
  }

  const orderShipmentRows = () => {
    return orderShipments.orderShipments.map((shipmentRecord) => {
      const shipment = shipmentRecord.shipment || {};
      const items = shipment.items || [];

      return {
        id: shipmentRecord.order_shipment_id,
        orderId: Number(shipmentRecord.order_id) || 0,
        shipmentId: Number(shipmentRecord.shipment_id) || 0,
        order: `#${shipmentRecord.order_id}`,
        shipment: `#${shipmentRecord.shipment_id}`,
        location: locationLabel(shipmentRecord.location_id || shipment.location_id),
        status: shipmentRecord.status || shipment.status || '',
        destination: destination(shipment),
        items: `${items.length} lines / ${totalQuantity(items)} units`,
        blockers: shipmentBlockers(items, selectedLayout),
        detail: {
          order_id: shipmentRecord.order_id,
          shipment_id: shipmentRecord.shipment_id,
          location: shipmentRecord.location_id || shipment.location_id,
          shipment,
          items
        }
      }
    }).sort((a, b) => a.orderId - b.orderId || a.shipmentId - b.shipmentId)
  }

  const rows = orderShipmentRows();
  const disabledRowIds = new Set(rows.filter((row) => row.blockers.length > 0).map((row) => row.id));

  const {
    selectedResources: selectedShipmentIds,
    allResourcesSelected: allShipmentsSelected,
    handleSelectionChange: handleShipmentSelectionChange,
  } = useIndexResourceState(rows, { resourceIDResolver: (row) => row.id });

  const selectedIds = () => {
    return selectedShipmentIds.filter((id) => !disabledRowIds.has(id));
  }

  const handleLayoutSelect = (layoutId) => {
    const layout = cartLayouts.cartLayouts.find((entry) => entry.id === layoutId);
    setSelectedLayout(layout || null);
  }

  // Blocked shipments render dimmed so the row reads as disabled
  const shipmentCell = (row, content) => {
    if (!row.blockers?.length) return content;
    return <span style={{ opacity: 0.4 }}>{content}</span>;
  }

  const toteRows = () => {
    return totes.totes.filter((tote) => !selectedLayout || tote.cart_layout_id === selectedLayout.id).map((tote) => {
      const items = tote.items || [];

      return {
        id: tote.sort_key,
        order: `#${tote.order_id}`,
        shipment: `#${tote.shipment_id}`,
        items: `${items.length} lines / ${totalQuantity(items)} units`,
        tote
      }
    }).sort((a, b) => a.id.localeCompare(b.id))
  }

  const allToteRows = toteRows();

  const {
    selectedResources: selectedToteIds,
    handleSelectionChange: handleToteSelectionChange,
  } = useIndexResourceState(allToteRows, { resourceIDResolver: (row) => row.id });

  const selectedOrderShipmentIds = () => {
    const selected = new Set(selectedToteIds);
    const shipmentIds = totes.totes
      .filter((tote) => selected.has(tote.sort_key))
      .map((tote) => tote.order_shipment_id);

    return [...new Set(shipmentIds)];
  }

  const confirmPendingToteAction = () => {
    const action = pendingToteAction === 'undo' ? undoTotes : deleteTotes;

    setPendingToteAction(null);
    action(selectedOrderShipmentIds()).then((result) => {
      if (result) {
        loadOrderShipments();
        loadTotes();
      }
    });
  }

  const lastPrepareResultMessage = () => {
    const result = orderShipments.lastPrepareResult;

    if (!result) {
      return null;
    }

    return `Prepared ${result.prepared_shipment_count} shipments from ${result.prepared_order_count} orders, skipped ${result.skipped_prepared_order_count} prepared orders, and marked ${result.marked_order_count} orders with no shippable fulfillments.`;
  }

  const prepareAllMessage = () => {
    const summary = orderShipments.prepareAllSummary;

    if (!summary) {
      return null;
    }

    return `Prepared ${summary.prepared_shipment_count} shipments across ${summary.pages_prepared} source pages. Checked ${summary.order_count} orders, skipped ${summary.skipped_prepared_order_count} already-prepared orders, and marked ${summary.marked_order_count} orders with no shippable fulfillments.`;
  }

  // Shipments the backend refused to convert under the selected layout
  const unconvertibleMessages = () => {
    const unconvertible = orderShipments.lastConvertResult?.unconvertible_order_shipments || [];

    return unconvertible.map((entry) => `${entry.order_shipment_id}: ${(entry.reasons || []).map((reason) => reason.message || reason).join(' ')}`);
  }

  const shipmentRowMarkup = rows.map((row, index) => (
    <IndexTable.Row
      id={row.id}
      key={row.id}
      position={index}
      selected={selectedShipmentIds.includes(row.id)}
      disabled={disabledRowIds.has(row.id)}
    >
      <IndexTable.Cell>
        {row.blockers?.length ? (
          <Tooltip content={row.blockers.map((blocker) => blocker.message).join(' ')}>
            <span><Icon source={AlertTriangleIcon} tone="warning" /></span>
          </Tooltip>
        ) : null}
      </IndexTable.Cell>
      <IndexTable.Cell>{shipmentCell(row, row.order)}</IndexTable.Cell>
      <IndexTable.Cell>{shipmentCell(row, row.shipment)}</IndexTable.Cell>
      <IndexTable.Cell>{shipmentCell(row, row.location)}</IndexTable.Cell>
      <IndexTable.Cell>{shipmentCell(row, row.status)}</IndexTable.Cell>
      <IndexTable.Cell>{shipmentCell(row, row.destination)}</IndexTable.Cell>
      <IndexTable.Cell>
        {shipmentCell(row, <Button variant="plain" onClick={() => setSelectedDetail(row.detail)}>{row.items}</Button>)}
      </IndexTable.Cell>
    </IndexTable.Row>
  ));

  const toteRowMarkup = allToteRows.map((row, index) => (
    <IndexTable.Row
      id={row.id}
      key={row.id}
      position={index}
      selected={selectedToteIds.includes(row.id)}
    >
      <IndexTable.Cell>{row.order}</IndexTable.Cell>
      <IndexTable.Cell>{row.shipment}</IndexTable.Cell>
      <IndexTable.Cell>
        <Button variant="plain" onClick={() => setSelectedDetail(row.tote)}>{row.items}</Button>
      </IndexTable.Cell>
    </IndexTable.Row>
  ));

  return(
    <Page
      title="Convert Order Shipments to Totes"
      subtitle="Prepare awaiting-fulfillment Shopify order shipments and convert them into totes for warehouse picking. Have questions or need some help? Contact us at support@athousandapps.com"
    >
      <Layout>
        <Layout.Section variant="oneHalf">
          <Card>
            <BlockStack gap="400">
              <Text as="h2" variant="headingMd">Prepared Order Shipments</Text>
              <Text as="p" variant="bodyMd">Fetch awaiting-fulfillment orders from Shopify and store their fulfillment consignments for tote generation.</Text>

              { cartLayouts.status === 'success' && cartLayouts.cartLayouts.length === 0 &&
                <Banner
                  title="No cart layouts"
                  tone="warning"
                >
                  <p>Create a cart layout before converting order shipments to totes.</p>
                </Banner>
              }

              { cartLayouts.cartLayouts.length > 0 &&
                <Select
                  label="Cart Layout"
                  options={cartLayouts.cartLayouts.map((layout) => ({ label: layout.label, value: layout.id }))}
                  value={selectedLayout?.id || ''}
                  onChange={handleLayoutSelect}
                />
              }

              <InlineStack align="start">
                <Button
                  icon={TransferInternalIcon}
                  variant="primary"
                  onClick={() => {
                    convertOrderShipmentsToTotes(selectedIds(), selectedLayout?.id).then((result) => {
                      if (result) {
                        loadOrderShipments();
                        loadTotes();
                      }
                    })
                  }}
                  disabled={isBusy || selectedIds().length === 0 || !selectedLayout}
                >
                  Convert {selectedIds().length} Shipments to Totes
                </Button>
              </InlineStack>

              { orderShipments.errors &&
                <Banner
                  title="Order shipment request failed"
                  tone="critical"
                >
                  <p>{orderShipments.errors}</p>
                </Banner>
              }

              { unconvertibleMessages().length > 0 &&
                <Banner
                  title="Shipments not converted"
                  tone="warning"
                >
                  {unconvertibleMessages().map((text, index) => <p key={index}>{text}</p>)}
                </Banner>
              }

              { (orderShipments.isPreparing || orderShipments.isPreparingAll || orderShipments.isConverting) &&
                <LoadingSpinner
                  size="small"
                  statusText={orderShipments.isConverting ? 'Converting order shipments to totes' : orderShipments.isPreparingAll ? 'Preparing all Shopify order shipment pages' : 'Preparing the next page of Shopify orders'}
                />
              }

              { lastPrepareResultMessage() &&
                <Banner tone="info">
                  <p>{lastPrepareResultMessage()}</p>
                </Banner>
              }

              { prepareAllMessage() &&
                <Banner tone="success">
                  <p>{prepareAllMessage()}</p>
                </Banner>
              }

              { orderShipments.prepareLastPage &&
                <Banner tone="warning">
                  <p>All awaiting-fulfillment order pages have been checked. There are no remaining order shipment pages to prepare.</p>
                </Banner>
              }

              { disabledRowIds.size > 0 &&
                <Banner
                  title="Shipments too large for selected cart layout"
                  tone="warning"
                >
                  <p>{`${disabledRowIds.size} shipment${disabledRowIds.size === 1 ? '' : 's'} can't be converted with the ${selectedLayout?.label || 'selected'} cart layout and ${disabledRowIds.size === 1 ? 'has' : 'have'} been deselected. Hover a warning icon for details.`}</p>
                </Banner>
              }

              <InlineStack gap="300" wrap>
                <Button
                  icon={RefreshIcon}
                  onClick={() => prepareOrderShipmentPage(orderShipments.nextPrepareCursor)}
                  disabled={isBusy || orderShipments.prepareLastPage}
                  loading={orderShipments.isPreparing}
                >
                  Prepare Next Page
                </Button>
                <Button
                  icon={UploadIcon}
                  onClick={() => prepareAllOrderShipments()}
                  disabled={isBusy || orderShipments.prepareLastPage}
                  loading={orderShipments.isPreparingAll}
                >
                  Prepare All Order Shipments
                </Button>
                <Button
                  variant="plain"
                  icon={UndoIcon}
                  onClick={() => resetPrepare()}
                  disabled={isBusy}
                >
                  Reset Order Search
                </Button>
              </InlineStack>

              { orderShipments.orderShipments.length > 0 &&
                <Box paddingBlockStart="200">
                  <IndexTable
                    resourceName={{ singular: 'shipment', plural: 'shipments' }}
                    itemCount={rows.length}
                    selectedItemsCount={allShipmentsSelected ? 'All' : selectedShipmentIds.length}
                    onSelectionChange={handleShipmentSelectionChange}
                    headings={[
                      { title: '' },
                      { title: 'Order' },
                      { title: 'Shipment' },
                      { title: 'Location' },
                      { title: 'Status' },
                      { title: 'Destination' },
                      { title: 'Items' },
                    ]}
                  >
                    {shipmentRowMarkup}
                  </IndexTable>
                </Box>
              }

              { (orderShipments.isFetching || orderShipments.isFetchingMore) &&
                <LoadingSpinner size="small" statusText="Loading prepared order shipments" />
              }

              { !isBusy && orderShipments.status === 'success' && orderShipments.orderShipments.length === 0 &&
                <Banner tone="warning">
                  <p>No prepared order shipments. Prepare order shipments from Shopify to see them here.</p>
                </Banner>
              }
            </BlockStack>
          </Card>
        </Layout.Section>

        <Layout.Section variant="oneHalf">
          <Card>
            <BlockStack gap="400">
              <Text as="h2" variant="headingMd">Unassigned Totes</Text>
              <Text as="p" variant="bodyMd">Totes generated from prepared order shipments that have not yet been assigned to a batch.</Text>

              <InlineStack align="start">
                <Button
                  icon={TransferInternalIcon}
                  variant="primary"
                  onClick={() => {
                    setAssignedToBatches(false);
                    assignTotes(selectedLayout?.id, selectedToteIds).then((result) => {
                      if (result) {
                        setAssignedToBatches(true);
                        loadTotes();
                      }
                    })
                  }}
                  disabled={totesBusy || selectedToteIds.length === 0 || !selectedLayout}
                >
                  Assign {selectedToteIds.length} Totes to Batches
                </Button>
              </InlineStack>

              <InlineStack gap="300" wrap>
                <Button
                  icon={UndoIcon}
                  onClick={() => setPendingToteAction('undo')}
                  disabled={totesBusy || selectedToteIds.length === 0}
                >
                  Undo Tote Creation
                </Button>
                <Button
                  tone="critical"
                  icon={DeleteIcon}
                  onClick={() => setPendingToteAction('delete')}
                  disabled={totesBusy || selectedToteIds.length === 0}
                >
                  Delete Totes
                </Button>
              </InlineStack>

              { assignedToBatches &&
                <Banner tone="success">
                  <p>Totes assigned to batches. Go to the <Link to="/batches">Active Batches page</Link> to see them.</p>
                </Banner>
              }

              { totes.errors &&
                <Banner
                  title="Tote request failed"
                  tone="critical"
                >
                  <p>{totes.errors}</p>
                </Banner>
              }

              { (totes.lastActionResult?.skipped_order_shipment_ids || []).length > 0 &&
                <Banner
                  title="Some order shipments were skipped"
                  tone="warning"
                >
                  {totes.lastActionResult.skipped_order_shipment_ids.map((id) => (
                    <p key={id}>{id}: has totes assigned to a batch. Remove them from the batch before retrying.</p>
                  ))}
                </Banner>
              }

              { allToteRows.length > 0 &&
                <Box paddingBlockStart="200">
                  <IndexTable
                    resourceName={{ singular: 'tote', plural: 'totes' }}
                    itemCount={allToteRows.length}
                    selectedItemsCount={selectedToteIds.length}
                    onSelectionChange={handleToteSelectionChange}
                    headings={[
                      { title: 'Order' },
                      { title: 'Shipment' },
                      { title: 'Items' },
                    ]}
                  >
                    {toteRowMarkup}
                  </IndexTable>
                </Box>
              }

              { (totes.isFetching || totes.isFetchingMore) &&
                <LoadingSpinner size="small" statusText="Loading unassigned totes" />
              }

              { !totes.isFetching && !totes.isFetchingMore && totes.status === 'success' && allToteRows.length === 0 &&
                <Banner tone="warning">
                  <p>No unassigned totes. Convert prepared order shipments to totes to see them here.</p>
                </Banner>
              }
            </BlockStack>
          </Card>
        </Layout.Section>
      </Layout>

      <Modal
        open={pendingToteAction !== null}
        onClose={() => setPendingToteAction(null)}
        title={pendingToteAction === 'undo' ? 'Undo Tote Creation' : 'Delete Totes'}
        primaryAction={{
          content: pendingToteAction === 'undo' ? 'Undo Tote Creation' : 'Delete Totes',
          destructive: pendingToteAction === 'delete',
          onAction: confirmPendingToteAction
        }}
        secondaryActions={[
          {
            content: 'Cancel',
            onAction: () => setPendingToteAction(null),
          },
        ]}
      >
        <Modal.Section>
          <BlockStack gap="300">
            <Text as="p">
              This will apply to all totes associated with the selected totes' order shipments, not just the selected totes.
              Pack Perfect can't pick or pack partial order shipments.
            </Text>
            { pendingToteAction === 'undo' &&
              <Text as="p">
                All totes for the affected order shipments will be deleted and the shipments will return to the prepared order shipments list, where they can be converted to totes again.
              </Text>
            }
            { pendingToteAction === 'delete' &&
              <Text as="p">
                All totes for the affected order shipments will be permanently deleted. The order shipments will remain marked as converted and will not be processed through this app.
              </Text>
            }
          </BlockStack>
        </Modal.Section>
      </Modal>

      <Modal
        open={selectedDetail !== null}
        onClose={() => setSelectedDetail(null)}
        title={selectedDetail ? `Order #${selectedDetail.order_id} / Shipment #${selectedDetail.shipment_id}` : ''}
        primaryAction={{
          content: 'Close',
          onAction: () => setSelectedDetail(null),
        }}
      >
        <Modal.Section>
          { selectedDetail &&
            <BlockStack gap="400">
              <InlineStack gap="800" align="start" wrap>
                <BlockStack gap="200">
                  <Text as="h3" variant="headingSm">Shipment</Text>
                  <Text as="p">Status: {selectedDetail.shipment?.status || '-'}</Text>
                  <Text as="p">Location: {locationLabel(selectedDetail.location)}</Text>
                  <Text as="p">Shipping method: {selectedDetail.shipment?.shipping_method || '-'}</Text>
                  { selectedDetail.shipment?.order_comments &&
                    <Text as="p">Order comments: {selectedDetail.shipment.order_comments}</Text>
                  }
                  { selectedDetail.shipment?.staff_notes &&
                    <Text as="p">Staff notes: {selectedDetail.shipment.staff_notes}</Text>
                  }
                </BlockStack>
                <BlockStack gap="200">
                  <Text as="h3" variant="headingSm">Shipping Address</Text>
                  { formatAddress(selectedDetail.shipment?.shipping_address).map((line, i) => <Text as="p" key={i}>{line}</Text>) }
                </BlockStack>
              </InlineStack>

              <Text as="h3" variant="headingSm">Items</Text>
              <IndexTable
                resourceName={{ singular: 'item', plural: 'items' }}
                itemCount={(selectedDetail.items || []).length}
                selectable={false}
                headings={[
                  { title: 'Item' },
                  { title: 'SKU' },
                  { title: 'UPC' },
                  { title: 'BPN' },
                  { title: 'Qty' },
                ]}
              >
                {(selectedDetail.items || []).map((item, index) => (
                  <IndexTable.Row id={`${item.id || item.sku || index}`} key={index} position={index}>
                    <IndexTable.Cell>{item.name}</IndexTable.Cell>
                    <IndexTable.Cell>{item.sku || '-'}</IndexTable.Cell>
                    <IndexTable.Cell>{item.upc || '-'}</IndexTable.Cell>
                    <IndexTable.Cell>{item.bpn || '-'}</IndexTable.Cell>
                    <IndexTable.Cell>{item.quantity}</IndexTable.Cell>
                  </IndexTable.Row>
                ))}
              </IndexTable>
            </BlockStack>
          }
        </Modal.Section>
      </Modal>
    </Page>
  )
}

const mapStateToProps = state => ({
  orderShipments: state.orderShipments,
  totes: state.totes,
  cartLayouts: state.cartLayouts,
  locations: state.locations,
  settings: state.settings,
});

const mapDispatchToProps = (dispatch) => ({
  loadOrderShipments: () => dispatch(orderShipmentsIndex()),
  loadTotes: () => dispatch(totesIndex()),
  loadCartLayouts: () => dispatch(cartLayoutsIndex()),
  loadLocations: () => dispatch(locationsIndex()),
  loadSettings: () => dispatch(getSettings()),
  prepareOrderShipmentPage: (cursor) => dispatch(orderShipmentPrepare(cursor)),
  prepareAllOrderShipments: () => dispatch(orderShipmentsPrepareAll()),
  convertOrderShipmentsToTotes: (orderShipmentIds, cartLayoutId) => dispatch(orderShipmentsConvertToTotes(orderShipmentIds, cartLayoutId)),
  resetPrepare: () => dispatch(orderShipmentsPrepareReset()),
  assignTotes: (cartLayoutId, toteIds) => dispatch(assignTotesToBatches(cartLayoutId, toteIds)),
  undoTotes: (orderShipmentIds) => dispatch(totesUndo(orderShipmentIds)),
  deleteTotes: (orderShipmentIds) => dispatch(totesDelete(orderShipmentIds)),
});

export default connect(mapStateToProps, mapDispatchToProps)(OrderShipments);
